import { useMemo, useCallback, useEffect, useState } from 'react';
import electronBridge from '../utils/electronBridge';

/**
 * React hook for detecting and managing Electron environment features
 * 
 * @returns {Object} Environment information and feature flags
 * @returns {boolean} isElectron - Whether the app is running in Electron
 * @returns {boolean} isWeb - Whether the app is running in a web browser
 * @returns {Object} features - Feature availability flags
 * @returns {string} platform - Platform information (darwin, win32, linux, web)
 * @returns {boolean} isDevelopment - Whether the app is in development mode
 * @returns {Function} canUseFeature - Check if a specific feature is available
 * @returns {Function} requireElectron - Show error if feature requires Electron
 * @returns {Object} platformInfo - Detailed platform information
 */
export const useElectronEnvironment = () => {
  // Get initial environment state with safe defaults
  const [environment, setEnvironment] = useState(() => electronBridge.getEnvironment() || {});
  
  // Re-check environment on mount (in case it changes)
  useEffect(() => {
    // Force a fresh environment check
    const env = electronBridge.getEnvironment();
    setEnvironment(env);
    
    // Log environment info in development
    if (env.isDevelopment) {
      console.log('Electron Environment:', {
        isElectron: env.isElectron,
        platform: env.platform,
        features: env.features,
        capabilities: Object.entries(env).filter(([k, v]) => typeof v === 'boolean' && v)
      });
    }
  }, []);
  
  // Memoize environment data with safe defaults
  const environmentData = useMemo(() => ({
    isElectron: environment?.isElectron || false,
    isWeb: environment?.isWeb !== false,
    platform: environment?.platform || 'web',
    isDevelopment: environment?.isDevelopment || false,
    features: environment?.features || {},
    hasNodeIntegration: environment?.hasNodeIntegration || false,
    hasIPC: environment?.hasIPC || false,
    hasSQLite: environment?.hasSQLite || false,
    hasFileSystem: environment?.hasFileSystem || false,
    hasShell: environment?.hasShell || false,
  }), [environment]);
  
  // Check if a specific feature is available
  const canUseFeature = useCallback((feature) => {
    return electronBridge.canUseFeature(feature);
  }, []);
  
  // Show error message for Electron-only features
  const requireElectron = useCallback((featureName, customMessage) => {
    if (!environment?.isElectron) {
      const message = customMessage || 
        `${featureName} requires the desktop application. Please download and install the Electron app to use this feature.`;
      
      console.warn(`[Electron Required] ${message}`);
      
      return {
        available: false,
        message,
        showDesktopOnlyUI: true
      };
    }
    
    return {
      available: true,
      message: null,
      showDesktopOnlyUI: false
    };
  }, [environment?.isElectron]);
  
  // Get detailed platform information
  const platformInfo = useMemo(() => electronBridge.getPlatformInfo(), []);
  
  // Helper to check multiple features at once
  const hasAllFeatures = useCallback((...features) => {
    return features.every(feature => canUseFeature(feature));
  }, [canUseFeature]);
  
  // Helper to check if any of the features are available
  const hasAnyFeature = useCallback((...features) => {
    return features.some(feature => canUseFeature(feature));
  }, [canUseFeature]);
  
  // Get user-friendly feature descriptions
  const getFeatureDescription = useCallback((feature) => {
    const descriptions = {
      fileOperations: 'File system operations',
      directorySelection: 'Directory selection',
      sqliteDatabase: 'Local database (MOMM)',
      comfyUIIntegration: 'ComfyUI integration',
      nativeAuth: 'Native authentication',
      localFileUpload: 'Local file upload',
      imageMetadataEdit: 'Image metadata editing',
      shellOperations: 'External application launching',
      logging: 'Application logging'
    };
    
    return descriptions[feature] || feature;
  }, []);
  
  // Get list of unavailable features with descriptions
  const getUnavailableFeatures = useCallback(() => {
    if (environment.isElectron) {
      return [];
    }
    
    return Object.entries(environment.features)
      .filter(([_, available]) => !available)
      .map(([feature]) => ({
        feature,
        description: getFeatureDescription(feature)
      }));
  }, [environment, getFeatureDescription]);
  
  return {
    // Core environment flags
    ...environmentData,
    
    // Utility functions
    canUseFeature,
    requireElectron,
    hasAllFeatures,
    hasAnyFeature,
    
    // Platform information
    platformInfo,
    
    // Feature descriptions
    getFeatureDescription,
    getUnavailableFeatures,
    
    // Direct access to electronBridge for advanced usage
    electronBridge
  };
};

// Hook for showing desktop-only messages
export const useDesktopOnlyMessage = (feature, customMessage) => {
  const { requireElectron } = useElectronEnvironment();
  
  return useMemo(() => {
    return requireElectron(feature, customMessage);
  }, [feature, customMessage, requireElectron]);
};

// Hook for feature-gated functionality
export const useFeatureGate = (feature, fallbackBehavior) => {
  const { canUseFeature } = useElectronEnvironment();
  const isAvailable = canUseFeature(feature);
  
  const execute = useCallback((electronFunction, ...args) => {
    if (isAvailable) {
      return electronFunction(...args);
    } else if (fallbackBehavior) {
      return fallbackBehavior(...args);
    } else {
      console.warn(`Feature '${feature}' is not available in this environment`);
      return Promise.resolve({ 
        success: false, 
        error: 'Feature not available',
        requiresElectron: true 
      });
    }
  }, [isAvailable, feature, fallbackBehavior]);
  
  return {
    isAvailable,
    execute,
    feature
  };
};

// Hook for conditional rendering based on environment
export const useEnvironmentConditional = () => {
  const { isElectron, isWeb } = useElectronEnvironment();
  
  const renderForElectron = useCallback((component) => {
    return isElectron ? component : null;
  }, [isElectron]);
  
  const renderForWeb = useCallback((component) => {
    return isWeb ? component : null;
  }, [isWeb]);
  
  const renderConditional = useCallback((electronComponent, webComponent) => {
    return isElectron ? electronComponent : webComponent;
  }, [isElectron]);
  
  return {
    renderForElectron,
    renderForWeb,
    renderConditional
  };
};

export default useElectronEnvironment;